import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip, Skeleton, keyframes } from '@mui/material';
import { m } from 'framer-motion';
import { Iconify } from '../index';

// ── Count-up hook: animates a number from 0 → target ──
function useCountUp(target, duration = 800, enabled = true) {
    const [current, setCurrent] = useState(enabled ? 0 : target);
    const rafRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        if (!enabled) { setCurrent(target || 0); return undefined; }
        if (typeof target !== 'number' || Number.isNaN(target) || target === 0) {
            setCurrent(target || 0);
            return undefined;
        }
        startTimeRef.current = null;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - (1 - progress) ** 3; // ease-out cubic
            setCurrent(Math.round(target * eased));
            if (progress < 1) rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, duration, enabled]);

    return current;
}

// ── Framer-motion variants for staggered card entrance ──
const containerVariants = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.08, delayChildren: 0.3 },
    },
};

const cardMotionVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.97 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
    },
};

// Helpful tooltips for each widget type
const WIDGET_TOOLTIPS = {
    '1': 'New leads waiting to be contacted. Reach out quickly to maximize conversion.',
    '2': 'Follow up with new patients who haven\'t booked a consultation yet.',
    '3': 'Patients with upcoming consultations. Ensure they\'re prepared and confirmed.',
    '4': 'Patients who attended consultation but haven\'t started treatment. Follow up to close the sale.',
    '5': 'ClinChecks ready to be sent to patients for approval.',
    '6': 'Patients who missed their consultation. Re-engage them to reschedule.',
    '7': 'WhatsApp messages needing a response. Quick replies improve patient satisfaction.',
    'BOOKED_AWAITING': 'Consultations that have passed but attendance hasn\'t been recorded. Record outcome to keep data accurate.',
};

// Urgency state constants
const URGENCY_STATES = {
    URGENT: 'urgent',
    WARNING: 'warning',
    GOOD: 'good',
    NEUTRAL: 'neutral',
};

// Priority order for sorting
const PRIORITY_ORDER = {
    [URGENCY_STATES.URGENT]: 1,
    [URGENCY_STATES.WARNING]: 2,
    [URGENCY_STATES.GOOD]: 3,
    [URGENCY_STATES.NEUTRAL]: 4,
};

// Animation keyframes
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const shimmer = keyframes`
    0% {
        background-position: -200px 0;
    }
    100% {
        background-position: calc(200px + 100%) 0;
    }
`;

// Styles matching the mockup
const cardWrapperStyle = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    width: '100%',
    mb: 3,
};

const getCardStyle = (urgencyState, isSelected, isLoaded, isFirstLoad) => {
    const baseStyle = {
        flex: '1 1 0',
        minWidth: '140px',
        minHeight: '110px',
        padding: '16px',
        borderRadius: '12px',
        border: '2px solid #e2e8f0',
        '@media (max-width: 576px)': {
            flex: '1 1 calc(50% - 6px)',
            minWidth: 'unset',
        },
        background: '#fff',
        cursor: 'pointer',
        transition: 'border-color 0.3s ease, background 0.3s ease, box-shadow 0.2s ease, transform 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        // Skip CSS animation when framer-motion handles the entrance
        animation: (!isFirstLoad && isLoaded) ? `${fadeInUp} 0.35s ease-out` : 'none',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
    };

    // Non-selected: no border/bg override — left accent bar is rendered separately
    // Selected: full colored border + gradient + shadow
    const urgencyColors = {
        [URGENCY_STATES.URGENT]: '#ef4444',
        [URGENCY_STATES.WARNING]: '#f59e0b',
        [URGENCY_STATES.GOOD]: '#10b981',
        [URGENCY_STATES.NEUTRAL]: '#e2e8f0',
    };

    const color = urgencyColors[urgencyState] || '#e2e8f0';

    const selectedStyle = isSelected ? {
        border: `2px solid ${color}`,
        background: `linear-gradient(to bottom, ${color}14, #fff)`,
        boxShadow: `0 4px 12px ${color}20`,
        transform: 'translateY(-2px)',
    } : {};

    return { ...baseStyle, ...selectedStyle };
};

// Left colored accent bar style
const getLeftBarStyle = (urgencyState) => {
    const colors = {
        [URGENCY_STATES.URGENT]: '#ef4444',
        [URGENCY_STATES.WARNING]: '#f59e0b',
        [URGENCY_STATES.GOOD]: '#10b981',
        [URGENCY_STATES.NEUTRAL]: 'transparent',
    };

    return {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: '4px',
        background: colors[urgencyState] || 'transparent',
        borderRadius: '10px 0 0 10px',
    };
};

const cardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    mb: '4px',
};

// Unified text colors per urgency (icon, number, label all match)
const URGENCY_TEXT_COLORS = {
    [URGENCY_STATES.URGENT]: '#dc2626',
    [URGENCY_STATES.WARNING]: '#b45309',
    [URGENCY_STATES.GOOD]: '#059669',
    [URGENCY_STATES.NEUTRAL]: '#475569',
};

const URGENCY_ICON_BG = {
    [URGENCY_STATES.URGENT]: '#fef2f2',
    [URGENCY_STATES.WARNING]: '#fffbeb',
    [URGENCY_STATES.GOOD]: '#ecfdf5',
    [URGENCY_STATES.NEUTRAL]: '#f1f5f9',
};

const cardNumberStyle = {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1e293b',
    lineHeight: 1,
};

const getIconWrapperStyle = (bgColor) => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: bgColor || '#f1f5f9',
    flexShrink: 0,
});

const cardLabelStyle = {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: 1.3,
    mb: '10px',
};

const timeContextStyle = {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
};

const getTimeBadgeStyle = (type) => {
    const baseStyle = {
        fontSize: '10px',
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: '10px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };

    const typeStyles = {
        overdue: {
            background: '#fee2e2',
            color: '#dc2626',
        },
        'due-today': {
            background: '#fef3c7',
            color: '#d97706',
        },
        'on-track': {
            background: '#dcfce7',
            color: '#059669',
        },
        info: {
            background: '#f1f5f9',
            color: '#64748b',
        },
    };

    return { ...baseStyle, ...typeStyles[type] };
};

const priorityDotStyle = (color) => ({
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    display: 'inline-block',
    bgcolor: color,
    flexShrink: 0,
});

// Skeleton card styles
const skeletonCardStyle = {
    flex: '1 1 0',
    minWidth: '140px',
    minHeight: '110px',
    padding: '16px',
    borderRadius: '12px',
    border: '2px solid #f1f5f9',
    background: '#fafbfc',
    position: 'relative',
    overflow: 'hidden',
    '@media (max-width: 576px)': {
        flex: '1 1 calc(50% - 6px)',
        minWidth: 'unset',
    },
};

const skeletonShimmerOverlay = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)`,
    backgroundSize: '200px 100%',
    backgroundRepeat: 'no-repeat',
    animation: `${shimmer} 1.5s ease-in-out infinite`,
};

// Skeleton card component
const ActionCardSkeleton = () => (
    <Box sx={skeletonCardStyle}>
        <Box sx={skeletonShimmerOverlay} />
        <Box sx={cardHeaderStyle}>
            <Skeleton variant="text" width={48} height={32} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '8px' }} />
        </Box>
        <Skeleton variant="text" width="75%" height={16} sx={{ mb: '10px', borderRadius: '4px' }} />
        <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: '10px' }} />
    </Box>
);

// TimeBadge Component
const TimeBadge = ({ type, text, showDot = false, dotColor = '#ef4444' }) => (
    <Box component="span" sx={getTimeBadgeStyle(type)}>
        {showDot && <Box component="span" sx={priorityDotStyle(dotColor)} />}
        {text}
    </Box>
);

TimeBadge.propTypes = {
    type: PropTypes.oneOf(['overdue', 'due-today', 'on-track', 'info']).isRequired,
    text: PropTypes.string.isRequired,
    showDot: PropTypes.bool,
    dotColor: PropTypes.string,
};


// ActionCard Component
const ActionCard = ({ card, onClick = null, isSelected = false, isLoaded = false, isFirstLoad = false }) => {
    const { count, displayCount, label, icon, iconBgColor, urgencyState, key, tooltip } = card;
    const badges = card.badges || [];
    // Use tooltip from card data, fall back to internal WIDGET_TOOLTIPS
    const tooltipText = tooltip || WIDGET_TOOLTIPS[key] || '';
    // Use displayCount if provided (e.g., "20+"), otherwise use count
    const shownCount = displayCount !== undefined ? displayCount : count;

    // Animate numbers on first load (count up from 0); skip for string counts like "20+"
    const numericCount = typeof shownCount === 'number' ? shownCount : 0;
    const animatedCount = useCountUp(numericCount, 800, isFirstLoad);
    const displayedCount = isFirstLoad && typeof shownCount === 'number' ? animatedCount : shownCount;

    // Unified urgency text color — icon, number, and label all match
    const textColor = URGENCY_TEXT_COLORS[urgencyState] || '#475569';
    const iconBg = URGENCY_ICON_BG[urgencyState] || iconBgColor || '#f1f5f9';

    const cardContent = (
        <Box sx={getCardStyle(urgencyState, isSelected, isLoaded, isFirstLoad)} onClick={() => onClick?.(card)}>
            {/* Left accent bar */}
            {urgencyState !== URGENCY_STATES.NEUTRAL && (
                <Box sx={getLeftBarStyle(urgencyState)} />
            )}

            <Box sx={cardHeaderStyle}>
                <Typography sx={{ ...cardNumberStyle, color: textColor }}>{displayedCount}</Typography>
                {icon && (
                    <Box sx={getIconWrapperStyle(iconBg)}>
                        <Iconify icon={icon} width={16} height={16} sx={{ color: textColor }} />
                    </Box>
                )}
            </Box>
            <Typography sx={{ ...cardLabelStyle, color: textColor }}>{label}</Typography>
            {badges.length > 0 && (
                <Box sx={timeContextStyle}>
                    {badges.map((badge) => (
                        <TimeBadge
                            key={badge.type}
                            type={badge.type}
                            text={badge.text}
                            showDot={badge.showDot}
                            dotColor={badge.dotColor}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );

    if (tooltipText) {
        return (
            <Tooltip
                title={tooltipText}
                arrow
                placement="top"
                enterDelay={500}
                slotProps={{
                    tooltip: {
                        sx: {
                            bgcolor: '#1e293b',
                            fontSize: '12px',
                            maxWidth: 220,
                            textAlign: 'center',
                            '& .MuiTooltip-arrow': {
                                color: '#1e293b',
                            },
                        },
                    },
                }}
            >
                {cardContent}
            </Tooltip>
        );
    }

    return cardContent;
};

ActionCard.propTypes = {
    card: PropTypes.shape({
        key: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        displayCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        label: PropTypes.string.isRequired,
        icon: PropTypes.string,
        iconBgColor: PropTypes.string,
        urgencyState: PropTypes.oneOf(Object.values(URGENCY_STATES)),
        badges: PropTypes.arrayOf(
            PropTypes.shape({
                type: PropTypes.oneOf(['overdue', 'due-today', 'on-track', 'info']),
                text: PropTypes.string,
                showDot: PropTypes.bool,
                dotColor: PropTypes.string,
            })
        ),
        tooltip: PropTypes.string,
    }).isRequired,
    onClick: PropTypes.func,
    isSelected: PropTypes.bool,
    isLoaded: PropTypes.bool,
    isFirstLoad: PropTypes.bool,
};


// Main ActionCards Component
const ActionCards = ({ cards, onCardClick = null, sortByUrgency = true, selectedKey = null, loading = false, loadedKeys = [], skeletonCount = 6, isFirstLoad = false, forceSkeletons = false }) => {
    // Sort cards by urgency if enabled
    const sortedCards = sortByUrgency
        ? [...cards].sort((a, b) => {
            const priorityDiff =
                PRIORITY_ORDER[a.urgencyState || URGENCY_STATES.NEUTRAL] -
                PRIORITY_ORDER[b.urgencyState || URGENCY_STATES.NEUTRAL];
            if (priorityDiff !== 0) return priorityDiff;
            // Secondary sort by count (descending) for same priority
            return b.count - a.count;
        })
        : cards;

    // Show skeleton cards while loading and no cards have data yet,
    // or when forceSkeletons is true (first session waiting for all data)
    const showSkeletons = forceSkeletons || (loading && sortedCards.length === 0);
    const skeletonSlots = skeletonCount || 6;

    // Use framer-motion stagger container on first load, plain Box otherwise
    const WrapperComponent = isFirstLoad ? m.div : 'div';
    const wrapperProps = isFirstLoad
        ? { variants: containerVariants, initial: 'hidden', animate: 'visible' }
        : {};

    return (
        <Box component={WrapperComponent} {...wrapperProps} sx={cardWrapperStyle}>
            {showSkeletons ? (
                // Render skeleton placeholders while waiting for first data
                Array.from({ length: skeletonSlots }).map((_, index) => (
                    <ActionCardSkeleton key={`skeleton-${index}`} />
                ))
            ) : (
                sortedCards.map((card) => {
                    const cardEl = (
                        <ActionCard
                            key={card.key}
                            card={card}
                            onClick={onCardClick}
                            isSelected={selectedKey === card.key}
                            isLoaded={loadedKeys?.includes(card.key)}
                            isFirstLoad={isFirstLoad}
                        />
                    );
                    // Wrap each card in a motion div for stagger when first load
                    if (isFirstLoad) {
                        return (
                            <m.div key={card.key} variants={cardMotionVariants} style={{ flex: '1 1 0' }}>
                                {cardEl}
                            </m.div>
                        );
                    }
                    return cardEl;
                })
            )}
        </Box>
    );
};

ActionCards.propTypes = {
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            count: PropTypes.number.isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.string,
            iconBgColor: PropTypes.string,
            urgencyState: PropTypes.oneOf(Object.values(URGENCY_STATES)),
            badges: PropTypes.array,
        })
    ).isRequired,
    onCardClick: PropTypes.func,
    sortByUrgency: PropTypes.bool,
    selectedKey: PropTypes.string,
    loading: PropTypes.bool,
    loadedKeys: PropTypes.array,
    skeletonCount: PropTypes.number,
    isFirstLoad: PropTypes.bool,
    forceSkeletons: PropTypes.bool,
};


export { ActionCards, ActionCard, ActionCardSkeleton, TimeBadge, URGENCY_STATES };
export default ActionCards;
